import type { FloorPlanDocument, FloorPlanPoint } from "@/lib/shows/floorPlan";

export type ShowMapPinGroup = { tableIds: readonly string[]; };
export type ShowMapPin = {
  anchorX: number;
  anchorY: number;
  groupIndex: number;
  hasLeader: boolean;
  x: number;
  y: number;
};

const ENTRANCE = /(?:^|\s)(?:step-free\s+)?entrance(?:\s|$)/i;
const DEFAULT_MAP_SIZE = { width: 390, height: 200 };
const MAP_PADDING = 10;
const PIN_DIAMETER = 28;
const PIN_GAP = 4;
const RELAXATION_PASSES = 24;

function average(points: readonly FloorPlanPoint[]): FloorPlanPoint | null {
  if (!points.length) return null;
  return {
    x: points.reduce((sum, point) => sum + point.x, 0) / points.length,
    y: points.reduce((sum, point) => sum + point.y, 0) / points.length,
  };
}

function polygonCentroid(polygon: readonly FloorPlanPoint[]): FloorPlanPoint | null {
  if (polygon.length < 3) return average(polygon);
  let crossSum = 0;
  let x = 0;
  let y = 0;
  for (let index = 0; index < polygon.length; index += 1) {
    const point = polygon[index];
    const next = polygon[(index + 1) % polygon.length];
    const cross = point.x * next.y - next.x * point.y;
    crossSum += cross;
    x += (point.x + next.x) * cross;
    y += (point.y + next.y) * cross;
  }
  return Math.abs(crossSum) < Number.EPSILON
    ? average(polygon)
    : { x: x / (3 * crossSum), y: y / (3 * crossSum) };
}

function tableCentre(
  document: FloorPlanDocument,
  tableIds: readonly string[],
): FloorPlanPoint | null {
  const wanted = new Set(tableIds);
  return average(
    document.tables.filter((table) => wanted.has(table.id)).map((table) => table.center),
  );
}

function distanceToSegment(
  point: FloorPlanPoint,
  start: FloorPlanPoint,
  end: FloorPlanPoint,
): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (dx === 0 && dy === 0) return Math.hypot(point.x - start.x, point.y - start.y);
  const amount = Math.max(
    0,
    Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)),
  );
  return Math.hypot(point.x - (start.x + amount * dx), point.y - (start.y + amount * dy));
}

function distanceToPolygon(point: FloorPlanPoint, polygon: readonly FloorPlanPoint[]): number {
  if (!polygon.length) return Infinity;
  return Math.min(
    ...polygon.map((start, index) =>
      distanceToSegment(point, start, polygon[(index + 1) % polygon.length])
    ),
  );
}

/** Describes assigned tables from the attendee's entrance-facing view of the published hall. */
export function describeShowLocation(
  document: FloorPlanDocument,
  tableIds: readonly string[],
): string | null {
  const target = tableCentre(document, tableIds);
  const hallCentre = polygonCentroid(document.boundary);
  if (!target || !hallCentre) return null;

  const entranceArea = document.areas.find((area) => !area.obstructs && ENTRANCE.test(area.name));
  const entrance = entranceArea
    ? polygonCentroid(entranceArea.polygon)
    : (() => {
      const xs = document.boundary.map((point) => point.x);
      const ys = document.boundary.map((point) => point.y);
      return {
        x: (Math.min(...xs) + Math.max(...xs)) / 2,
        y: Math.max(...ys),
      };
    })();
  if (!entrance) return null;

  const inward = { x: hallCentre.x - entrance.x, y: hallCentre.y - entrance.y };
  const length = Math.hypot(inward.x, inward.y) || 1;
  const forward = { x: inward.x / length, y: inward.y / length };
  const left = { x: forward.y, y: -forward.x };
  const offset = { x: target.x - hallCentre.x, y: target.y - hallCentre.y };
  const depth = offset.x * forward.x + offset.y * forward.y >= 0 ? "back" : "front";
  const side = offset.x * left.x + offset.y * left.y >= 0 ? "left" : "right";
  const quadrant = `${depth}-${side}`;

  const obstruction = document.areas
    .filter((area) => area.obstructs && area.name.trim())
    .map((area) => ({ area, distance: distanceToPolygon(target, area.polygon) }))
    .sort((a, b) => a.distance - b.distance || a.area.id.localeCompare(b.area.id))[0]?.area;
  return obstruction
    ? `${quadrant}, past the ${obstruction.name.trim().toLocaleLowerCase()}`
    : quadrant;
}

/** Screen-space pins, kept deterministic so a displaced marker never silently hides another. */
export function layoutShowMapPins(
  document: FloorPlanDocument,
  groups: readonly ShowMapPinGroup[],
  textScale = 1,
  size = DEFAULT_MAP_SIZE,
): ShowMapPin[] {
  if (!Number.isFinite(textScale) || textScale <= 0 || document.boundary.length < 3) return [];
  const xs = document.boundary.map((point) => point.x);
  const ys = document.boundary.map((point) => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const worldWidth = Math.max(1, Math.max(...xs) - minX);
  const worldHeight = Math.max(1, Math.max(...ys) - minY);
  const fit = Math.min(
    (size.width - 2 * MAP_PADDING) / worldWidth,
    (size.height - 2 * MAP_PADDING) / worldHeight,
  );
  const shownWidth = worldWidth * fit;
  const shownHeight = worldHeight * fit;
  const offsetX = (size.width - shownWidth) / 2;
  const offsetY = (size.height - shownHeight) / 2;
  const pins = groups.flatMap((group, groupIndex) => {
    const centre = tableCentre(document, group.tableIds);
    return centre
      ? [{
        anchorX: offsetX + (centre.x - minX) * fit,
        anchorY: offsetY + (centre.y - minY) * fit,
        groupIndex,
        x: offsetX + (centre.x - minX) * fit,
        y: offsetY + (centre.y - minY) * fit,
      }]
      : [];
  });

  const scale = Math.max(1, textScale);
  const minimum = (PIN_DIAMETER + PIN_GAP) * scale;
  for (let pass = 0; pass < RELAXATION_PASSES; pass += 1) {
    let moved = false;
    for (let first = 0; first < pins.length; first += 1) {
      for (let second = first + 1; second < pins.length; second += 1) {
        const a = pins[first];
        const b = pins[second];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const distance = Math.hypot(dx, dy);
        if (distance >= minimum) continue;
        moved = true;
        const x = distance === 0 ? 1 : dx / distance;
        const y = distance === 0 ? 0 : dy / distance;
        const push = (minimum - distance) / 2;
        a.x -= x * push;
        a.y -= y * push;
        b.x += x * push;
        b.y += y * push;
      }
    }
    if (!moved) break;
  }

  const radius = (PIN_DIAMETER * scale) / 2 + 2;
  for (const pin of pins) {
    pin.x = Math.min(size.width - radius, Math.max(radius, pin.x));
    pin.y = Math.min(size.height - radius, Math.max(radius, pin.y));
  }
  return pins.map((pin) => ({
    ...pin,
    hasLeader: Math.hypot(pin.x - pin.anchorX, pin.y - pin.anchorY) > 3,
  }));
}

/** True only when the bounded separation pass still leaves overlapping scaled pins. */
export function shouldCollapseShowMap(
  document: FloorPlanDocument,
  groups: readonly ShowMapPinGroup[],
  textScale: number,
  size = DEFAULT_MAP_SIZE,
): boolean {
  const pins = layoutShowMapPins(document, groups, textScale, size);
  if (
    !Number.isFinite(textScale) || textScale <= 0 || document.boundary.length < 3
    || (groups.length > 0 && pins.length === 0)
  ) return true;
  const minimum = (PIN_DIAMETER + PIN_GAP) * Math.max(1, textScale);
  return pins.some((a, first) =>
    pins.slice(first + 1).some((b) => Math.hypot(b.x - a.x, b.y - a.y) < minimum - 0.5)
  );
}
